<template>
  <section id="hmo" class="hmo-section">
    <v-container>
      <v-row align="center">
        <v-col cols="12" lg="5">
          <div class="hmo-copy">
            <div class="eyebrow">HMO & accreditations</div>
            <h2>HMO partners, accreditations, and quality-focused affiliations.</h2>
            <p>
              Mayon works with selected HMO partners and institutional affiliations, and participates in external quality
              assurance programs to support confidence in reported patient results. Coverage, approval, and service
              availability may vary depending on the patient account and requested service.
            </p>
            <v-alert text color="#4870c8" class="hmo-copy__alert">Coverage, approval, requested service, schedule, and branch availability must be confirmed with the branch before visiting.</v-alert>
            <div class="section-links">
              <router-link to="/hmo-partners" v-if="!standalone">View all HMO partners</router-link>
              <router-link to="/services">Browse services</router-link>
              <router-link to="/branches">Contact branches</router-link>
            </div>
          </div>
        </v-col>

        <v-col cols="12" lg="7">
          <div class="logo-panel">
            <h3>HMO Partners</h3>
            <div class="logo-grid logo-grid--hmo">
              <div v-for="partner in hmoPartners" :key="partner.name" class="logo-card">
                <img :src="partner.image" :alt="`${partner.name} logo`" />
                <span>{{ partner.name }}</span>
              </div>
            </div>

            <h3 class="mt-8">Accreditations & affiliations</h3>
            <div class="logo-grid logo-grid--small">
              <div v-for="item in accreditations" :key="item.name" class="logo-card">
                <img :src="item.image" :alt="`${item.name} logo`" />
                <span>{{ item.name }}</span>
              </div>
            </div>
          </div>
        </v-col>
      </v-row>
    </v-container>
  </section>
</template>

<script>
import { accreditations, hmoPartners } from '@/data/mayonSite'

export default {
  name: 'hmoAccredited',
  props: {
    standalone: { type: Boolean, default: false }
  },
  data() {
    return {
      accreditations,
      hmoPartners
    }
  }
}
</script>

<style lang="scss" scoped>
.hmo-section {
  background: var(--mayon-bg);
  padding: 5rem 0;
}

.hmo-copy {
  max-width: 520px;

  .eyebrow {
    color: var(--mayon-blue);
    font-size: 0.82rem;
    font-weight: 800;
    letter-spacing: 0.09em;
    margin-bottom: 0.7rem;
    text-transform: uppercase;
  }

  h2 {
    color: var(--mayon-navy);
    font-size: clamp(2rem, 4vw, 3rem);
    letter-spacing: -0.035em;
    line-height: 1.1;
    margin-bottom: 1rem;
  }

  p {
    color: var(--mayon-muted);
    line-height: 1.8;
  }

  &__alert {
    background: rgba(72, 112, 200, 0.08) !important;
    border: 1px solid rgba(72, 112, 200, 0.12) !important;
    border-radius: 14px !important;
    margin-top: 1rem;
  }
}

.logo-panel {
  background: linear-gradient(180deg, #ffffff 0%, #fbfdff 100%);
  border: 1px solid var(--mayon-border);
  border-radius: 28px;
  box-shadow: var(--mayon-shadow-card);
  padding: 1.5rem;

  h3 {
    color: var(--mayon-navy);
    font-size: 1.05rem;
    margin-bottom: 1rem;
  }
}

.logo-grid {
  display: grid;
  gap: 0.8rem;

  &--hmo {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }

  &--small {
    grid-template-columns: repeat(5, minmax(0, 1fr));
  }
}

.section-links {
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  margin-top: 1.25rem;

  a {
    color: var(--mayon-blue);
    font-weight: 800;
    text-decoration: none;
  }
}

.logo-card {
  align-items: center;
  background: linear-gradient(180deg, #ffffff 0%, #fbfdff 100%);
  border: 1px solid var(--mayon-border);
  border-radius: 18px;
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
  justify-content: center;
  min-height: 96px;
  padding: 0.95rem;
  transition: border-color 0.18s ease, transform 0.18s ease, box-shadow 0.18s ease;

  &:hover {
    border-color: rgba(88, 184, 184, 0.34);
    box-shadow: 0 12px 28px rgba(10, 27, 52, 0.05);
    transform: translateY(-2px);
  }

  span {
    color: var(--mayon-navy);
    font-size: 0.78rem;
    font-weight: 800;
    line-height: 1.25;
    text-align: center;
  }

  img {
    display: block;
    filter: drop-shadow(0 1px 0 rgba(10, 27, 52, 0.50)) drop-shadow(0 5px 12px rgba(10, 27, 52, 0.25));
    max-height: 58px;
    max-width: 88%;
    object-fit: contain;
  }
}

.logo-grid--small .logo-card {
  min-height: 86px;

  span {
    color: var(--mayon-navy);
    font-size: 0.78rem;
    font-weight: 800;
    line-height: 1.25;
    text-align: center;
  }

  img {
    max-height: 54px;
  }
}

@media (max-width: 700px) {
  .logo-grid--hmo,
  .logo-grid--small {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}
</style>
